import { useCallback, useEffect, useState } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";
import ConfirmDialog from "components/admin/ConfirmDialog";
import StatusBadge from "components/admin/StatusBadge";
import { apiDelete, apiGet, apiPost } from "lib/apiClient";
import { useAuth } from "contexts/AuthContext";

export default function PartnerTeamPage() {
  const { portal, user: me } = useAuth();
  const canManage = Boolean(portal?.canManage);

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [invite, setInvite] = useState({ name: "", email: "" });
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addNotice, setAddNotice] = useState(null);

  const [pendingRemove, setPendingRemove] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await apiGet("/portal/partner/team");
      setPartner(result.partner);
    } catch (err) {
      setError(err.message || "Failed to load your team.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function addMember() {
    setAddError(null);
    setAddNotice(null);
    if (invite.name.trim().length < 2) return setAddError("Enter their name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invite.email.trim())) return setAddError("Enter a valid email address.");
    setAddBusy(true);
    try {
      const result = await apiPost("/portal/partner/team", { name: invite.name.trim(), email: invite.email.trim() });
      setAddNotice(
        result.createdUser
          ? `${invite.email.trim()} has been emailed a link to this portal and a temporary password. They will choose their own at first sign-in.`
          : `${invite.email.trim()} already had an account and can now sign in to this portal with it.`,
      );
      setInvite({ name: "", email: "" });
      await load();
    } catch (err) {
      setAddError(err.message || "Could not add that person.");
    } finally {
      setAddBusy(false);
    }
  }

  async function confirmRemove() {
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await apiDelete(`/portal/partner/team/${pendingRemove.userId}`);
      setPendingRemove(null);
      await load();
    } catch (err) {
      setRemoveError(err.message || "Failed to remove that teammate.");
    } finally {
      setRemoveBusy(false);
    }
  }

  const members = partner?.members || [];

  return (
    <div>
      {canManage && (
        <Card extra="p-5 mb-5">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Add a teammate</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            They get an email with a link to this portal and a temporary password. Teammates can see your markets and revenue; only you manage the team.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <InputField label="Full name" placeholder="e.g. Tom Otieno" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
            <InputField label="Email" placeholder="name@yourcompany.com" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
          </div>
          {addError && <p className="mt-2 text-sm font-medium text-red-500">{addError}</p>}
          {addNotice && <p className="mt-2 text-sm font-medium text-green-600">{addNotice}</p>}
          <button type="button" onClick={addMember} disabled={addBusy} className="mt-4 rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
            {addBusy ? "Sending invite…" : "Send invite"}
          </button>
        </Card>
      )}

      <Card extra="p-5">
        <p className="text-lg font-semibold text-navy-700 dark:text-white">Team</p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Everyone with access to this portal for {partner?.name || "your organization"}.
        </p>
        {error && <div className="mt-3 rounded-xl bg-red-50 p-3 text-sm font-medium text-red-500">{error}</div>}
        {loading && !partner && <div className="mt-4 h-24 animate-pulse rounded-md bg-accent" />}
        {partner && members.length === 0 && <p className="mt-4 text-sm text-gray-500">No team members yet.</p>}
        {members.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[480px] text-left">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="pb-2 pr-3 font-semibold">Email</th>
                  <th className="pb-2 pr-3 font-semibold">Role</th>
                  <th className="pb-2 pr-3 font-semibold">Status</th>
                  {canManage && <th className="pb-2 text-right font-semibold">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.userId} className="border-t border-gray-100 dark:border-white/5">
                    <td className="py-2.5 pr-3">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-navy-700 dark:text-white">{m.email || m.userId}</p>
                        {me?.id === m.userId && <StatusBadge label="You" tone="blue" />}
                      </div>
                    </td>
                    <td className="py-2.5 pr-3">
                      {m.isPrimaryContact ? <StatusBadge label="Primary contact (admin)" tone="green" /> : <StatusBadge label="Teammate (view only)" tone="gray" />}
                    </td>
                    <td className="py-2.5 pr-3">
                      {m.mustChangePassword ? <StatusBadge label="Invited, not signed in yet" tone="amber" /> : <StatusBadge label="Active" tone="green" />}
                    </td>
                    {canManage && (
                      <td className="py-2.5 text-right">
                        {!m.isPrimaryContact && me?.id !== m.userId && (
                          <button
                            type="button"
                            onClick={() => setPendingRemove({ userId: m.userId, email: m.email })}
                            className="rounded-sm bg-red-50 px-3 py-2 text-xs font-semibold text-red-500"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!pendingRemove}
        title="Remove this teammate?"
        description={`${pendingRemove?.email || "They"} will immediately lose access to this portal.`}
        confirmLabel="Remove"
        tone="danger"
        busy={removeBusy}
        error={removeError}
        onConfirm={confirmRemove}
        onCancel={() => {
          setPendingRemove(null);
          setRemoveError(null);
        }}
      />
    </div>
  );
}
